import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { PDKAddIcon, PDKAuctionIcon } from '@/components/ui/pdk-icons';
import { useUpdateTradeAuctionsMutation } from '@/hooks/use-update-trade-auctions-mutation';
import { cn } from '@/lib/utils';
import type { Trade } from '@/models/registry';
import { auctionStore } from '@/models/stores';
import type { Auction } from '@/models/types';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import CreateTradeAuctionDialog from './create-trade-auction-dialog';

type SelectedAuctions = Auction['id'][];
const INITIAL_AUCTIONS: SelectedAuctions = [];

const TradeAuctionsEditor = observer(({ trade }: { trade: Trade }) => {
  const { updateTradeAuctionsMutation } = useUpdateTradeAuctionsMutation(
    trade.id,
  );

  const [selectedAuctions, setSelectedAuctions] = useState<SelectedAuctions>(
    () => {
      if (!trade.auctions) {
        return INITIAL_AUCTIONS;
      }

      return trade.auctions;
    },
  );

  const [comboBoxOpen, setComboBoxOpen] = useState(false);

  useEffect(() => {
    if (!trade.auctions) {
      return;
    }

    setSelectedAuctions(trade.auctions);
  }, [trade.auctions]);

  const [createAuctionDialogOpen, setCreateAuctionDialogOpen] = useState(false);

  const comboBoxOptions = useMemo(() => {
    const auctionOptions = auctionStore.all.map((auction) => ({
      label: (
        <div className="flex flex-row items-center gap-2 text-nowrap">
          {auction.name}
        </div>
      ),
      value: auction.name,
      icon: (
        <div className="size-4">
          <Checkbox
            checked={selectedAuctions.includes(auction.id)}
            onCheckedChange={() => {
              handleAuctionToggle(auction.name);
            }}
          />
        </div>
      ),
    }));

    return [...auctionOptions];
  }, [selectedAuctions, auctionStore.all]);

  const handleAuctionToggle = (auctionName: string) => {
    // Handle create new auction
    if (auctionName === '__create_new__') {
      setCreateAuctionDialogOpen(true);
      setComboBoxOpen(false);
      return;
    }

    const auctionId = auctionStore.all.find(
      (auction) => auction.name === auctionName,
    )?.id;
    if (!auctionId) {
      return;
    }

    const newSelectedAuctions = selectedAuctions.includes(auctionId)
      ? selectedAuctions.filter((a) => a !== auctionId)
      : [...selectedAuctions, auctionId];

    setSelectedAuctions(newSelectedAuctions);

    updateTradeAuctionsMutation.mutate({
      auctions: newSelectedAuctions,
    });
  };

  const numberOfSelectedAuctions = selectedAuctions.length;

  const [search, setSearch] = useState('');

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-row gap-1 items-center">
        <div className="text-sm text-gray-500 dark:text-white">Auctions</div>
      </div>
      <div className="flex flex-row gap-1 items-center flex-wrap">
        <ComboboxPopover
          placeholder="Select auctions..."
          options={comboBoxOptions}
          onChange={handleAuctionToggle}
          open={comboBoxOpen}
          onOpenChange={setComboBoxOpen}
          stayOpenAfterSelection={true}
          popoverContentClassName={cn('w-fit min-w-[200px]')}
          endAdornment={undefined}
          emptyMessage={undefined}
          side="left"
          align="start"
          footerOptions={
            <CommandGroup>
              <CommandItem
                className="flex flex-row gap-2 items-center"
                onSelect={() => {
                  setCreateAuctionDialogOpen(true);
                }}
              >
                <PDKAddIcon className="h-3.5 w-3.5" />
                <p className="rounded text-xs  text-gray-600">
                  Create new auction{' '}
                  {search && <span className="text-gray-500">"{search}"</span>}
                </p>
              </CommandItem>
            </CommandGroup>
          }
          alwaysShowValues={[`Create new auction "${search}"`]}
          search={search}
          setSearch={setSearch}
        >
          <div>
            {/* Intentional empty div to make the combobox popover have a stable position */}
          </div>
        </ComboboxPopover>
        {selectedAuctions.map((auctionId) => {
          const auction = auctionStore.getById(auctionId);
          if (!auction) return null;
          return (
            <Badge
              key={auctionId}
              variant="outline"
              className="flex flex-row gap-1 items-center"
            >
              <div className="text-sm text-gray-500 dark:text-white">
                {auction.name}
              </div>
            </Badge>
          );
        })}
        <div>
          <Button
            variant="ghost"
            className={cn('flex items-center h-fit w-fit p-1')}
            onClick={() => setComboBoxOpen(true)}
          >
            {numberOfSelectedAuctions > 0 ? (
              <PDKAddIcon className="h-4 w-4" />
            ) : (
              <div className="flex flex-row gap-1 items-center">
                <PDKAuctionIcon className="h-4 w-4 text-gray-500 dark:text-white" />
                <div className="text-sm text-gray-500 dark:text-white">
                  Add auction
                </div>
              </div>
            )}
          </Button>
        </div>
      </div>
      <CreateTradeAuctionDialog
        initialName={search}
        open={createAuctionDialogOpen}
        onOpenChange={setCreateAuctionDialogOpen}
        onSuccess={(auctionId) => {
          // Add the new auction to selected auctions
          setSelectedAuctions((prev) => {
            const newSelectedAuctions = [...prev, auctionId];
            updateTradeAuctionsMutation.mutate({
              auctions: newSelectedAuctions,
            });
            return newSelectedAuctions;
          });
        }}
      />
    </div>
  );
});

export default TradeAuctionsEditor;
